// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ChatMessage.test.tsx
// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/AskV2RightRail.test.tsx
import {
  CircleNotch,
  DownloadSimple,
  File as FileIcon,
  FileDoc,
  FileImage,
  FilePdf,
  FilePpt,
  FileText,
  FileVideo,
  FileXls,
} from "@phosphor-icons/react";
import { Button, Tooltip } from "@radix-ui/themes";
import { useRef, useState, type ReactNode } from "react";

import { downloadAsset } from "../utils/http";

const DOCUMENT_ICON_CLASS = "text-puntt-neutral-gray-11";

function documentFileIcon(
  type: string,
  name: string,
): { Icon: typeof FileIcon; kind: string } {
  const ext = name.includes(".")
    ? name.split(".").pop()?.toLowerCase()
    : undefined;
  switch (ext) {
    case "pdf":
    case "ai":
      return { Icon: FilePdf, kind: "pdf" };
    case "doc":
    case "docx":
      return { Icon: FileDoc, kind: "doc" };
    case "xls":
    case "xlsx":
    case "csv":
      return { Icon: FileXls, kind: "sheet" };
    case "ppt":
    case "pptx":
      return { Icon: FilePpt, kind: "slides" };
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
    case "svg":
      return { Icon: FileImage, kind: "image" };
    case "mp4":
    case "mov":
    case "avi":
    case "webm":
      return { Icon: FileVideo, kind: "video" };
    case "txt":
    case "md":
      return { Icon: FileText, kind: "text" };
    default:
      if (type === "image") return { Icon: FileImage, kind: "image" };
      if (type === "video") return { Icon: FileVideo, kind: "video" };
      return { Icon: FileIcon, kind: "file" };
  }
}

function DocumentDownloadName({
  name,
  testId,
  tooltipSide,
}: {
  name: string;
  testId: string;
  tooltipSide: "top" | "left";
}) {
  const nameRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <Tooltip content={name} side={tooltipSide} open={open} delayDuration={200}>
      <span
        ref={nameRef}
        className="min-w-0 flex-1 truncate font-medium"
        data-testid={testId}
        onMouseEnter={() => {
          const el = nameRef.current;
          // why: a name that already fits has nothing to reveal — same gate as nav recents
          if (el) setOpen(el.scrollWidth > el.clientWidth);
        }}
        onMouseLeave={() => setOpen(false)}
      >
        {name}
      </span>
    </Tooltip>
  );
}

/**
 * The context-pane reference document row: surface button, type icon that
 * swaps to download, truncated name. Chat attachments use the same row.
 */
export function DocumentDownloadRow({
  url,
  name,
  downloadAs,
  testId,
  ariaLabel,
  fileType = "file",
  tooltipSide = "top",
  trailing,
}: {
  url: string;
  name: string;
  /**
   * Filename sent to the download. Omit to use the visible name.
   * Null downloads without a filename (unnamed chat attachments).
   */
  downloadAs?: string | null;
  testId: string;
  ariaLabel: string;
  fileType?: string;
  tooltipSide?: "top" | "left";
  trailing?: ReactNode;
}) {
  const [downloading, setDownloading] = useState(false);
  // why: the button is not disabled until the next render, so a second click in this turn still sees downloading false
  const inFlight = useRef(false);
  const canDownload = Boolean(url.trim());
  const { Icon: TypeIcon, kind: typeKind } = documentFileIcon(fileType, name);

  const handleDownload = async () => {
    if (inFlight.current || !canDownload) return;
    inFlight.current = true;
    setDownloading(true);
    try {
      await downloadAsset(
        url,
        downloadAs === null ? undefined : (downloadAs ?? name),
      );
    } catch {
      // downloadAsset already handles user-visible failure paths.
    } finally {
      inFlight.current = false;
      setDownloading(false);
    }
  };

  return (
    <Button
      type="button"
      size="2"
      variant="surface"
      color="gray"
      highContrast
      onClick={() => {
        void handleDownload();
      }}
      disabled={downloading || !canDownload}
      data-testid={testId}
      aria-label={ariaLabel}
      // why: radix centers a content-sized button; this row has to fill its container and truncate
      className="group w-full !min-w-0 !shrink !justify-start !text-left"
    >
      <span
        aria-hidden="true"
        className="relative inline-flex size-4 shrink-0"
        data-testid={`${testId}-type`}
      >
        <TypeIcon
          size={16}
          className={`${DOCUMENT_ICON_CLASS} group-hover:!opacity-0 group-focus-visible:!opacity-0`}
          data-file-icon={typeKind}
        />
        {/* why: radix sets svg opacity, so the swap needs important to win */}
        <DownloadSimple
          size={16}
          className={`${DOCUMENT_ICON_CLASS} absolute inset-0 !opacity-0 group-hover:!opacity-100 group-focus-visible:!opacity-100`}
          data-testid={`${testId}-download`}
        />
      </span>
      <DocumentDownloadName
        name={name}
        testId={`${testId}-name`}
        tooltipSide={tooltipSide}
      />
      {trailing}
      {downloading ? (
        <CircleNotch
          size={14}
          className="shrink-0 animate-spin text-puntt-neutral-gray-9"
          aria-hidden="true"
        />
      ) : null}
    </Button>
  );
}
